import React from 'react';
import CrystalImage from 'components/crystal/crystalImage';
import { roundStringValue } from 'utils/Global';
import { Spinner } from 'react-bootstrap';
import {} from 'components/Experiment/utils';
import { ButtonsListContainer } from 'components/Button';
import {
  useCrystalAttachment,
  useCrystalDetails,
  useCrystalEdit,
} from 'components/crystal/hooks';
import {
  useSaveDiscardButtons,
  UseSaveDiscardButtonsParams,
} from 'CustomHooks/useSaveDiscardButtons';

interface CrystalDetailsProps {
  crystalId: number;
  editMode?: boolean;
  closeModal?: () => void;
  refetchSynchTrip?: () => void;
  isReadOnly?: boolean;
}

const CrystalDetails = (props: CrystalDetailsProps) => {
  const { crystalId, editMode, closeModal, refetchSynchTrip, isReadOnly } = props;
  const { crystal, setCrystal, isLoading, refetchCrystalDetails } =
    useCrystalDetails(crystalId);
  const { editCrystal, isDirty, handleChange, handleSave } = useCrystalEdit(
    crystal,
    setCrystal,
  );
  const { handleAttachmentUpload, handleAttachmentDownload } = useCrystalAttachment(
    crystal?.id,
    refetchCrystalDetails,
  );
  const onSave = () => {
    handleSave().then(() => {
      if (refetchSynchTrip) {
        refetchSynchTrip();
      }
      closeModal();
    });
  };

  const saveDiscardButtonsParams: UseSaveDiscardButtonsParams = {
    enableButtons: isDirty,
    onSave: onSave,
    onDiscard: closeModal,
    saveButtonText: 'Save',
    discardButtonText: 'Discard',
    alwaysEnableDiscard: true,
  };
  const saveDiscardButtons = useSaveDiscardButtons(saveDiscardButtonsParams);

  return (
    <>
      {!crystalId ? (
        <div
          id="crsytaldetails-empty"
          className="flex justify-center items-center h-full w-full"
        >
          <span id="crsytaldetails-empty-message" className="border px-5 py-5">
            Please select a Crystal.
          </span>
        </div>
      ) : (
        <>
          {isLoading ? (
            <div
              id="crsytaldetails-loading"
              className="w-full h-full mx-auto text-center pb-3 flex items-center justify-center"
            >
              <Spinner
                id="crsytaldetails-loading-spinner"
                animation="border"
                style={{ color: '#F89723' }}
              />
            </div>
          ) : (
            <div
              id="crystaldetails-wrapper"
              className="mx-auto w-full h-full bg-slate-50 text-sm"
            >
              {editMode && (
                <div
                  id="crystaldetails-edit-header"
                  className="flex justify-between w-full h-20 items-center px-2 py-3 bg-gray-300"
                >
                  <div
                    id={`crystaldetails-edit-title-${crystal?.name}`}
                    className="m-0 truncate w-96 font-semibold text-xl"
                  >
                    {crystal?.name}
                  </div>
                  <div id="crystaldetails-edit-buttons" className="m-0">
                    <ButtonsListContainer buttonsList={saveDiscardButtons} />
                  </div>
                </div>
              )}
              <span id="crystaldetails-info-header" className="font-bold text-lg p-2">
                Crystal Information
              </span>
              <div id="crystaldetails-topsection" className="flex mt-2">
                <div
                  id="crystaldetails-leftinfo"
                  className="flex w-1/2 flex-col min-h-[18rem] overflow-y-auto px-2"
                >
                  <div
                    id="crystaldetails-field-id-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-id"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Crystal ID
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.id}-id`}
                      className="w-1/2 truncate text-gray-900"
                    >
                      {crystal?.id}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-name-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-name"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Crystal Name
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.name}-name`}
                      className="w-1/2 truncate text-gray-900"
                      title={crystal?.name}
                    >
                      {editMode ? (
                        <input
                          id="crystaldetails-input-name"
                          type="text"
                          name="name"
                          value={editCrystal?.name || ''}
                          onChange={handleChange}
                          readOnly={isReadOnly}
                        />
                      ) : (
                        crystal?.name
                      )}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-project-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-project"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Project
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.projectName}-project`}
                      className="w-1/2 truncate text-gray-900"
                    >
                      {crystal?.projectName}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-experiment-wrapper"
                    className="flex justify  -between"
                  >
                    <dt
                      id="crystaldetails-field-header-experiment"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Experiment
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.experimentName}-experiment`}
                      className="w-1/2 truncate text-gray-900"
                    >
                      {crystal?.experimentName}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-protein-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-protein"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Protein
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.proteinName}-protein`}
                      className="w-1/2 truncate text-gray-900"
                    >
                      {crystal?.proteinName}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-owner-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-owner"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Owner
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.ownerName}-owner`}
                      className="w-1/2 truncate text-gray-900"
                    >
                      {crystal?.ownerName}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-barcode-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-barcode"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Barcode, Well, & Drop
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.barcode}${crystal?.wellRowCol}-barcode`}
                      className="w-1/2 truncate text-gray-900"
                      title={`${crystal?.barcode} ${crystal?.wellRowCol}`}
                    >
                      {`${crystal?.barcode} ${crystal?.wellRowCol}`}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-synchrotron-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-synchrotron"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Synchrotron Trip
                    </dt>
                    <dd
                      id={`crystaldetails-field-${crystal?.synchTripName}-protein`}
                      className="w-1/2 truncate text-gray-900"
                      title={crystal?.synchTripName ?? '-'}
                    >
                      {crystal?.synchTripName ?? '-'}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-datecollected-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-datecollected"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Date Collected
                    </dt>
                    <dd
                      id={`crystaldetails-field-${
                        crystal?.dateCollected != null
                          ? new Date(crystal.dateCollected).toLocaleDateString('en-US')
                          : '-'
                      }-datacollected`}
                      className="w-1/2 truncate text-gray-900"
                    >
                      {crystal?.dateCollected != null
                        ? new Date(crystal.dateCollected).toLocaleDateString('en-US')
                        : '-'}
                    </dd>
                  </div>
                  <div
                    id="crystaldetails-field-datefrozen-wrapper"
                    className="flex justify-between"
                  >
                    <dt
                      id="crystaldetails-field-header-datefrozen"
                      className="w-1/2 font-medium text-gray-600"
                    >
                      Date Frozen
                    </dt>
                    <dd
                      id={`crystaldetails-field-${
                        crystal?.dateFrozen != null
                          ? new Date(crystal.dateFrozen).toLocaleDateString('en-US')
                          : '-'
                      }-datafrozen`}
                      className="w-1/2 truncate text-gray-900"
                    >
                      {editMode ? (
                        <input
                          type="date"
                          name="dateFrozen"
                          value={editCrystal?.dateFrozen || ''}
                          onChange={handleChange}
                          readOnly={isReadOnly}
                        />
                      ) : (
                        <>
                          {crystal?.dateFrozen != null
                            ? new Date(crystal.dateFrozen).toLocaleDateString('en-US')
                            : '-'}
                        </>
                      )}
                    </dd>
                  </div>
                </div>
                <div
                  id="crystaldetails-image-wrapper"
                  className="flex w-1/2 justify-center items-center"
                >
                  <div
                    id="crystaldetails-image-container"
                    className="w-full h-full overflow-hidden"
                  >
                    <CrystalImage crystalId={crystalId} />
                  </div>
                </div>
              </div>
              <div
                id="crystaldetails-scrollarea"
                className={`overflow-auto ${editMode ? 'h-[25rem]' : 'h-full'}`}
              >
                <div
                  id="crystaldetails-protein-info-wrapper"
                  className="w-full p-2 min-h-fit"
                >
                  <span
                    id="crystaldetails-header-protein-info"
                    className="font-bold text-lg"
                  >
                    Protein Information
                  </span>
                  <div id="crystaldetails-protein-info-content" className="mt-2">
                    <div
                      id="crystaldetails-proteinformulation-wrapper"
                      className="flex justify-start"
                    >
                      <dt
                        id="crystaldetails-label-proteinformulation"
                        className="text-gray-600 font-medium w-[25%]"
                      >
                        Protein Formulation
                      </dt>
                      <dd
                        id={`crystaldetails-value-${crystal?.proteinName}-proteinformulation`}
                        className="text-gray-900 w-[75%]"
                      >
                        {crystal?.proteinName}
                      </dd>
                    </div>
                    <div
                      id="crystaldetails-proteiningredients-wrapper"
                      className="flex justify-start"
                    >
                      <dt
                        id="crystaldetails-label-proteiningredients"
                        className="text-gray-600 font-medium w-[25%]"
                      >
                        Protein Ingredients
                      </dt>
                      <dd
                        id="crystaldetails-value-proteiningredients"
                        className="text-gray-900 w-[75%]"
                      >
                        {crystal?.proteinFormulationDTO?.proteinFormulationIngredient?.map(
                          (pi) => (
                            <span
                              id={`crystaldetails-item-proteiningredient-${pi.id}`}
                              key={`pfIng-${pi.id}`}
                              className="block"
                            >
                              {pi.name +
                                ' ' +
                                pi.concentrationmM +
                                'mM' +
                                ' ' +
                                pi.concentrationmgmL +
                                'mg/mL'}
                            </span>
                          ),
                        )}
                      </dd>
                    </div>
                    <div
                      id="crystaldetails-standardingredients-wrapper"
                      className="flex justify-start"
                    >
                      <dt
                        id="crystaldetails-label-standardingredients"
                        className="text-gray-600 font-medium w-[25%]"
                      >
                        Standard Ingredients
                      </dt>
                      <dd
                        id="crystaldetails-value-standardingredients"
                        className="text-gray-900 w-[75%]"
                      >
                        {crystal?.proteinFormulationDTO?.proteinFormulationStandardIngredient?.map(
                          (si) => (
                            <span
                              id={`crystaldetails-item-standardingredient-${si.id}`}
                              key={`pfStandardIng-${si.id}`}
                              className="block"
                            >
                              {si.ingredientName +
                                ' ' +
                                si.concentration +
                                si.stockConcentrationUnit +
                                (si.pH ? si.pH + 'pH' : '')}
                            </span>
                          ),
                        )}
                      </dd>
                    </div>
                  </div>
                </div>
                <div
                  id="crystaldetails-additives-wrapper"
                  className="flex justify-between w-full min-h-fit"
                >
                  <div
                    id="crystaldetails-cryoprotectant-wrapper"
                    className="w-1/2 p-2 overflow-auto whitespace-nowrap"
                  >
                    <p
                      id="crystaldetails-cryoprotectant-header"
                      className="font-bold m-0 text-lg"
                    >
                      Cryoprotectant
                    </p>
                    {crystal?.cryoprotectants?.map((cryo) => (
                      <span
                        id={`crystaldetails-item-cryoprotectant-${cryo.id}`}
                        key={`crystal-cryo-${cryo.id}`}
                      >
                        {cryo.ingredient} {roundStringValue(cryo.conc)} {cryo.concUnit}{' '}
                        {cryo.ph} {cryo.protocol}
                      </span>
                    ))}
                  </div>
                  <div
                    id="crystaldetails-soaking-wrapper"
                    className="w-1/2 p-2 overflow-auto whitespace-nowrap"
                  >
                    <p
                      id="crystaldetails-soaking-header"
                      className="font-bold m-0 text-lg"
                    >
                      Soaking
                    </p>
                    {crystal?.soakings?.map((soaking) => (
                      <span
                        id={`crystaldetails-item-soaking-${soaking.id}`}
                        key={`soakingv4-${soaking.id}`}
                      >
                        {soaking.ingredient} {roundStringValue(soaking.conc)}{' '}
                        {soaking.concUnit} {soaking.time + soaking.timeUnit}{' '}
                        {soaking.textInfo}
                      </span>
                    ))}
                  </div>
                </div>
                <div
                  id="crystaldetails-notes-wrapper"
                  className="w-full min-h-fit p-2 whitespace-pre-wrap"
                >
                  <p id="crystaldetails-notes-header" className="font-bold m-0 text-lg">
                    Notes
                  </p>
                  {editMode ? (
                    <textarea
                      id="crystaldetails-notes-textarea"
                      value={editCrystal?.notes || ''}
                      name="notes"
                      onChange={handleChange}
                      className="w-full h-full select-text selection:bg-blue-500 selection:text-white"
                      readOnly={isReadOnly}
                    />
                  ) : (
                    <div
                      id="crystaldetails-notes-content"
                      className="border bg-white cursor-text"
                    >
                      {crystal?.notes}
                    </div>
                  )}
                </div>
                <div
                  id="crystaldetails-attachments-wrapper"
                  className="w-full min-h-fit p-2"
                >
                  <div
                    id="crystaldetails-attachments-inner"
                    className="flex flex-row justify-between"
                  >
                    <div id="crystaldetails-attachment-list-wrapper">
                      <p
                        id="crystaldetails-attachments-header"
                        className="font-bold m-0 text-lg"
                      >
                        Attachments
                      </p>
                      {crystal?.attachedFiles?.map((f) => (
                        <button
                          id={`crystaldetails-attachment-item-${f.id}`}
                          key={`attachFile${f.id}`}
                          onClick={() => handleAttachmentDownload(f.id)}
                          className="text-md m-0 mr-2 p-0 inline rounded-sm border-0 hover:underline bg-transparent max-w-md truncate"
                        >
                          {f.fileName}
                        </button>
                      ))}
                    </div>
                    {editMode && (
                      <div id="crystaldetails-attachment-upload-wrapper" className="h-8">
                        <label
                          id="crystaldetails-attachment-upload-label"
                          htmlFor="crystaldetails-attachment-file-upload"
                          className={`px-3 py-2 text-white font-bold rounded-md uppercase ${
                            isReadOnly
                              ? 'bg-gray-400 cursor-not-allowed opacity-60'
                              : 'bg-blue-500 hover:cursor-pointer'
                          }`}
                        >
                          Choose File
                        </label>
                        <input
                          id="crystaldetails-attachment-file-upload"
                          type="file"
                          onChange={handleAttachmentUpload}
                          style={{ display: 'none' }}
                          disabled={isReadOnly}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CrystalDetails;
